import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn.js';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium select-none transition-[background-color,color,border-color,transform] duration-[var(--nw-dur-feedback)] disabled:cursor-not-allowed disabled:opacity-50 active:translate-y-px [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-[var(--nw-primary)] text-[var(--nw-on-primary)] shadow-[0_8px_20px_-10px_rgb(0_0_0/0.5)] hover:opacity-90',
        secondary:
          'border border-[var(--nw-glass-edge)] bg-[var(--nw-glass-strong)] text-[var(--nw-ink)] backdrop-blur-md shadow-[inset_0_1px_0_var(--nw-glass-edge)] hover:bg-[var(--nw-surface)]',
        quiet: 'text-[var(--nw-ink)] hover:bg-[var(--nw-surface-sunk)]',
        danger: 'border border-[var(--nw-danger)] text-[var(--nw-danger)] hover:bg-[var(--nw-danger-soft)]',
        link: 'text-[var(--nw-river)] underline-offset-4 hover:underline px-0',
      },
      size: {
        sm: 'h-9 min-w-9 rounded-full px-3.5 text-sm',
        md: 'h-11 min-w-11 rounded-full px-5 text-[0.95rem]',
        lg: 'h-12 rounded-full px-6 text-base',
        icon: 'size-11 rounded-full',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  return <Comp ref={ref} type={asChild ? undefined : (type ?? 'button')} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});
